/**
 * Salir. Hasta hoy no existía ninguna forma: quien entraba con una cuenta se
 * quedaba con ella hasta que la cookie venciera sola, y en un teléfono
 * compartido —que es el teléfono de buena parte de la gente que se postula a
 * una H-2B— eso es la sesión de otro abierta.
 *
 * Salir de verdad son DOS cosas, y hacer sólo una es peor que no hacer nada:
 *
 *   1. borrar lo local: las cookies de acceso y refresco, y todo lo que esta
 *      app dejó en el navegador (el borrador del cuestionario, la agencia
 *      activa, los productos de la sesión, el usuario que guarda oidc-client).
 *   2. cerrar la sesión ARRIBA, en auth.tbit.app. Sin este paso, "Salir" y
 *      volver a entrar aterriza de nuevo en la misma cuenta sin preguntar
 *      nada —el proveedor todavía tiene su propia cookie— y la persona cree
 *      que la app se niega a soltarla.
 *
 * El orden importa: primero se borra lo local y después se navega, porque la
 * navegación se lleva la página por delante y lo que no se limpió antes no se
 * limpia nunca.
 *
 * ── EL end_session ──────────────────────────────────────────────────────────
 *
 * `client_id` y `post_logout_redirect_uri` van explícitos y sin `id_token_hint`:
 * el proveedor valida el destino contra los registrados para ESE cliente, y
 * `https://jobs.tbit.app` ya está registrado. Mandar un `id_token_hint` viejo
 * —el que quedó en sessionStorage puede ser de una sesión anterior— cambia una
 * pantalla de confirmación por una pantalla de error, así que no se manda.
 */
import { clearAuthCookie } from '~/lib/auth';

const AUTHORITY = import.meta.env.VITE_OIDC_AUTHORITY || 'https://auth.tbit.app';

/** El mismo cliente OIDC con el que se entró (ver `lib/oidc.ts`). */
const CLIENT_ID = 'platform-tbit-app';

/**
 * El destino después de cerrar. Es el origen de PRODUCCIÓN y no
 * `window.location.origin` a propósito: el proveedor sólo acepta destinos
 * registrados, y `localhost` no lo está. La variable existe para el día en que
 * se registre otro, no para adivinarlo en tiempo de ejecución.
 */
const POST_LOGOUT_REDIRECT_URI =
  import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI || 'https://jobs.tbit.app';

/**
 * Todo lo que esta app escribe en el navegador arranca con uno de estos. Se
 * borra por prefijo y no por lista de claves para que una clave nueva —otro
 * borrador, otra caché— no se quede viva por olvido de actualizar esta lista.
 */
const OWNED_PREFIXES = ['tbit.', 'oidc.', 'oidc_', 'job-apply:'];

function clearOwnedKeys(store: Storage): void {
  const doomed: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key && OWNED_PREFIXES.some((prefix) => key.startsWith(prefix))) doomed.push(key);
  }
  // En dos pasadas: borrar mientras se recorre corre los índices y salta claves.
  doomed.forEach((key) => store.removeItem(key));
}

/** La URL que cierra la sesión en el proveedor y devuelve a la portada. */
export function endSessionUrl(): string {
  const url = new URL('/session/end', AUTHORITY);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('post_logout_redirect_uri', POST_LOGOUT_REDIRECT_URI);
  return url.toString();
}

/**
 * Borra la sesión de ESTE navegador. Separada de `logout()` para que se pueda
 * probar sin navegar, y por si algún día hace falta limpiar sin salir.
 *
 * Nunca tira: en modo privado de Safari `sessionStorage` lanza al tocarlo, y
 * quedarse sin salir por eso sería el peor final posible de este botón.
 */
export function clearLocalSession(): void {
  try {
    clearAuthCookie();
  } catch {
    // Un documento sin cookies (sandbox) no es razón para abortar la salida.
  }
  for (const store of [window.sessionStorage, window.localStorage]) {
    try {
      clearOwnedKeys(store);
    } catch {
      // Idem: lo que importa es llegar al end_session.
    }
  }
}

/** Salir: limpia lo local y cierra la sesión arriba. No vuelve. */
export function logout(): void {
  clearLocalSession();
  window.location.assign(endSessionUrl());
}
